/**
 * rondo-web-forms
 *
 * Rondo Web Forms
 *
 * @author Jiri Hybek <jiri@hybek.cz>
 * @license See LICENSE file distributed with this source code.
 */

import { FormElement } from "../FormElement";
import { IFormElementConfig } from "../IFormElementConfig";
import { IFormElementFactory } from "../IFormElementFactory";
import { FormModel } from "../FormModel";
import { FORM_EL_TYPE } from "../FormElementTypes";
import { IFormExpressionFn } from "../IFormExpressionFn";

/**
 * Element config
 */
export interface IFormRowConfig extends IFormElementConfig {
	/** Section contents */
	content?: Array<IFormElementFactory<any>>;

	/** If to display a row */
	display?: IFormExpressionFn<FormRow, boolean>;

	/** If the row should be printable */
	print?: boolean;

	/** Color schema */
	colorSchema?: "default"|"heading"|"highlight";
}

/**
 * Row
 */
export class FormRow extends FormElement<IFormRowConfig> {

	/** Content elements */
	protected content: Array<FormElement<any>> = [];

	/** Display value */
	protected display: boolean;

	/**
	 * Element constructor
	 *
	 * @param model Model instance
	 * @param parent Parent element
	 * @param config Element configuration
	 */
	public constructor(
		protected model: FormModel,
		protected parent: FormElement<any>,
		protected config: IFormRowConfig
	) {

		super(model, parent, FORM_EL_TYPE.ROW, config);

		// Init content components
		if (config.content) {

			for (let i = 0; i < config.content.length; i++) {
				this.content.push(
					config.content[i](this.model, this)
				);
			}

		}

	}

	/**
	 * Updates the element
	 *
	 * @param resetState If to reset state
	 */
	public update(resetState: boolean = false) {

		// Call update on all content elements
		for (let i = 0; i < this.content.length; i++) {
			this.content[i].update(resetState);
		}

		// Add late computation if display is defined
		if (typeof this.config.display === "function") {
			this.model._addExpression(() => {

				this.display = this.config.display(this.model, this);
				this.emit("update");

			}, 1000);
		} else {
			this.display = true;
		}

	}

	/**
	 * Returns element state
	 */
	public getState() {

		return {
			...super.getState(),
			content: this.content,
			display: this.display
		};

	}

	/**
	 * Destroys the element
	 */
	public destroy() {

		// Call destroy on all content elements
		for (let i = 0; i < this.content.length; i++) {
			this.content[i].destroy();
		}

		super.destroy();

	}

	/**
	 * Get content element
	 */
	public getContent() {

		return this.content;

	}

}

/**
 * Declares section group
 *
 * @param config Config
 * @param content Content elements
 */
export function Row(
	config: IFormRowConfig,
	content: Array<IFormElementFactory<any>> = []
) {

	const _config = {
		content: content,
		...config
	};

	return (model, parent) => new FormRow(model, parent, _config);

}
